import {
  DIMENSION_TO_ROW_KEY,
  type LogisticPivotDimension,
  type LogisticPivotGroupDisplayMode,
  type LogisticPivotMetricColumnId,
  type LogisticPivotRow,
} from '../../models/LogisticPivot';
import type { PivotSortKey } from '../../utils/sortPivotRows';
import {
  buildPivotTree,
  collectBranchPaths,
  flattenVisibleTree,
  type PivotTreeNode,
} from '../../utils/buildPivotTree';
import { PivotMetricValue } from './PivotMetricValue';
import { cn } from '@shared/utils/utils';
import { ChevronDown, ChevronRight, ChevronsUpDown, ChevronUp } from 'lucide-react';
import { useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface LogisticPivotTableProps {
  rows: LogisticPivotRow[];
  dimensions: LogisticPivotDimension[];
  visibleMetrics: LogisticPivotMetricColumnId[];
  /** `hierarchy` muestra subtotales y plegado cuando hay 2+ ejes; `columns` siempre tabla plana. */
  groupDisplay: LogisticPivotGroupDisplayMode;
  isFetching?: boolean;
  sortKey: PivotSortKey | null;
  sortDir: 'asc' | 'desc';
  onSortChange: (key: PivotSortKey) => void;
}

export function LogisticPivotTable({
  rows,
  dimensions,
  visibleMetrics,
  groupDisplay,
  isFetching = false,
  sortKey,
  sortDir,
  onSortChange,
}: LogisticPivotTableProps) {
  const { t, i18n } = useTranslation('logisticPivotModule');

  const useHierarchyUi = groupDisplay === 'hierarchy' && dimensions.length > 1;

  const tree = useMemo(
    () =>
      useHierarchyUi
        ? buildPivotTree(
            rows,
            dimensions,
            sortKey ? { sortKey, sortDir } : undefined
          )
        : [],
    [rows, dimensions, useHierarchyUi, sortKey, sortDir]
  );

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  useLayoutEffect(() => {
    if (!useHierarchyUi) {
      setExpanded(new Set());
      return;
    }
    setExpanded(new Set(collectBranchPaths(tree)));
  }, [rows, dimensions, tree, useHierarchyUi]);

  const visibleRows = useMemo(
    () => (useHierarchyUi ? flattenVisibleTree(tree, expanded) : []),
    [tree, expanded, useHierarchyUi]
  );

  const togglePath = (path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const formatDimensionCell = (dim: LogisticPivotDimension, row: LogisticPivotRow): string => {
    if (dim === 'item') {
      const main = row.item_name?.trim() || row.item_code?.trim() || '—';
      if (
        row.item_code?.trim() &&
        row.item_name?.trim() &&
        row.item_code !== row.item_name
      ) {
        return `${main} (${row.item_code})`;
      }
      return main;
    }
    const key = DIMENSION_TO_ROW_KEY[dim];
    if (!key) return '—';
    const raw = row[key];
    if (raw === null || raw === undefined) return '—';
    if (dim === 'month' && typeof raw === 'number') {
      const d = new Date(Date.UTC(2000, raw - 1, 1));
      return d.toLocaleDateString(i18n.language, { month: 'short' });
    }
    if (dim === 'year' && typeof raw === 'number') {
      return String(raw);
    }
    return String(raw);
  };

  const dimensionCellText = (
    node: PivotTreeNode,
    dim: LogisticPivotDimension,
    colIndex: number
  ): string => {
    if (node.kind === 'leaf') {
      return formatDimensionCell(dim, node.row);
    }
    if (colIndex > node.depthIndex) return '—';
    return formatDimensionCell(dim, node.sampleRow);
  };

  const metricsSource = (node: PivotTreeNode): LogisticPivotRow => {
    if (node.kind === 'branch') {
      return node.metrics as unknown as LogisticPivotRow;
    }
    return node.row;
  };

  const showTreeChrome = useHierarchyUi;

  const flatRowKey = (row: LogisticPivotRow, index: number) =>
    dimensions.map((d) => formatDimensionCell(d, row)).join('\0') || `row-${index}`;

  const SortHeader = ({
    label,
    columnKey,
    align,
  }: {
    label: string;
    columnKey: PivotSortKey;
    align: 'left' | 'right';
  }) => {
    const active = sortKey === columnKey;
    return (
      <th
        scope="col"
        className={cn(
          'group relative px-3 py-2 text-left text-xs font-medium text-muted-foreground border-b border-border bg-background',
          align === 'right' && 'text-right'
        )}
      >
        <button
          type="button"
          onClick={() => onSortChange(columnKey)}
          className={cn(
            'inline-flex items-center gap-1 max-w-full rounded-sm transition-colors',
            'hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            align === 'right' && 'flex-row-reverse ml-auto'
          )}
        >
          <span className="truncate">{label}</span>
          <span
            className={cn(
              'shrink-0 text-muted-foreground',
              !active && 'opacity-0 group-hover:opacity-50 transition-opacity'
            )}
            aria-hidden
          >
            {active ? (
              sortDir === 'asc' ? (
                <ChevronUp className="h-4 w-4 text-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-foreground" />
              )
            ) : (
              <ChevronsUpDown className="h-3.5 w-3.5" />
            )}
          </span>
          <span className="sr-only">
            {active
              ? sortDir === 'asc'
                ? t('table.sort_active_asc')
                : t('table.sort_active_desc')
              : t('table.sort_column')}
          </span>
        </button>
      </th>
    );
  };

  return (
    <div
      className={cn(
        'w-full rounded-md border border-border bg-card overflow-hidden transition-opacity duration-300',
        isFetching && 'opacity-55 pointer-events-none'
      )}
      data-testid="logistic-pivot-table"
    >
      <div className="overflow-x-auto max-h-[min(70vh,720px)]">
        <table className="w-full border-collapse text-sm min-w-[520px]">
          <thead className="sticky top-0 z-10 bg-background">
            <tr>
              {dimensions.map((dim) => (
                <SortHeader
                  key={dim}
                  label={t(`dimensions.${dim}`)}
                  columnKey={`dim:${dim}`}
                  align="left"
                />
              ))}
              {visibleMetrics.map((id) => (
                <SortHeader
                  key={id}
                  label={t(`metrics.${id}`)}
                  columnKey={`metric:${id}`}
                  align="right"
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {!useHierarchyUi
              ? rows.map((row, rowIndex) => (
                  <tr
                    key={`flat-${rowIndex}-${flatRowKey(row, rowIndex)}`}
                    className="border-b border-border/40 last:border-b-0 transition-colors hover:bg-muted/20"
                  >
                    {dimensions.map((dim) => (
                      <td key={dim} className="px-3 py-2.5 align-middle whitespace-nowrap text-foreground">
                        {formatDimensionCell(dim, row)}
                      </td>
                    ))}
                    {visibleMetrics.map((id) => (
                      <td
                        key={id}
                        className="px-3 py-2 align-middle whitespace-nowrap text-right text-foreground"
                      >
                        <PivotMetricValue metricId={id} row={row} locale={i18n.language} />
                      </td>
                    ))}
                  </tr>
                ))
              : visibleRows.map(({ node, treeDepth }) => {
                  const m = metricsSource(node);
                  const isBranch = node.kind === 'branch';
                  const rowKey = node.path;

                  return (
                    <tr
                      key={rowKey}
                      className="border-b border-border/40 last:border-b-0 transition-colors hover:bg-muted/20"
                    >
                      {dimensions.map((dim, colIndex) => {
                        const text = dimensionCellText(node, dim, colIndex);
                        const isFirstDimCol = colIndex === 0;

                        return (
                          <td
                            key={dim}
                            className={cn(
                              'px-3 py-2.5 align-middle whitespace-nowrap text-foreground',
                              isFirstDimCol && showTreeChrome && isBranch && 'font-medium'
                            )}
                          >
                            {isFirstDimCol && showTreeChrome ? (
                              <div
                                className="flex items-center gap-1 min-w-0"
                                style={{ paddingLeft: treeDepth * 14 }}
                              >
                                {isBranch ? (
                                  <button
                                    type="button"
                                    onClick={() => togglePath(node.path)}
                                    className={cn(
                                      'shrink-0 flex h-7 w-7 items-center justify-center rounded-md',
                                      'text-muted-foreground hover:bg-muted/80 hover:text-foreground',
                                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                    )}
                                    aria-expanded={expanded.has(node.path)}
                                    aria-label={
                                      expanded.has(node.path)
                                        ? t('table.collapse_breakdown')
                                        : t('table.expand_breakdown')
                                    }
                                  >
                                    {expanded.has(node.path) ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </button>
                                ) : (
                                  <span
                                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center"
                                    aria-hidden
                                  />
                                )}
                                <span className="truncate">{text}</span>
                              </div>
                            ) : (
                              <span
                                className={cn(
                                  isBranch && colIndex > node.depthIndex && 'text-muted-foreground'
                                )}
                              >
                                {text}
                              </span>
                            )}
                          </td>
                        );
                      })}
                      {visibleMetrics.map((id) => (
                        <td
                          key={id}
                          className="px-3 py-2 align-middle whitespace-nowrap text-right text-foreground"
                        >
                          <PivotMetricValue metricId={id} row={m} locale={i18n.language} />
                        </td>
                      ))}
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
